import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  type MotionValue,
  type SpringOptions,
  AnimatePresence,
} from 'framer-motion';
import React, { Children, cloneElement, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReduceMotion } from '@/lib/useReduceMotion';

/**
 * macOS-style magnifying dock — adapted from ReactBits (Components/Dock). The
 * cursor-distance → size spring (the signature magnification) is kept verbatim;
 * adaptations for this project:
 *   - imports from 'framer-motion' (not 'motion/react')
 *   - simplified to an inline row with a fixed height (= magnification) and
 *     `items-end`, so icons grow UPWARD without reflowing the host bar (the
 *     original's height-animating floating panel fought our voice footer)
 *   - neutral base styling; per-item `className` drives the brand/state look
 */

export type DockItemData = {
  icon: React.ReactNode;
  label: React.ReactNode;
  onClick: () => void;
  className?: string;
};

export type DockProps = {
  items: DockItemData[];
  className?: string;
  distance?: number;
  baseItemSize?: number;
  magnification?: number;
  spring?: SpringOptions;
};

type DockItemProps = {
  className?: string;
  children: React.ReactNode;
  onClick?: () => void;
  mouseX: MotionValue<number>;
  spring: SpringOptions;
  distance: number;
  baseItemSize: number;
  magnification: number;
};

function DockItem({
  children,
  className = '',
  onClick,
  mouseX,
  spring,
  distance,
  magnification,
  baseItemSize,
}: DockItemProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isHovered = useMotionValue(0);

  // Cache the item's horizontal origin (rect.x). Dock items don't move
  // horizontally during magnification, so reading it per pointermove (inside the
  // transform) is pure layout thrash — measure once on mount + on resize.
  const itemXRef = useRef(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      itemXRef.current = el.getBoundingClientRect().x;
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  const mouseDistance = useTransform(mouseX, (val) => {
    return val - itemXRef.current - baseItemSize / 2;
  });

  const targetSize = useTransform(
    mouseDistance,
    [-distance, 0, distance],
    [baseItemSize, magnification, baseItemSize],
  );
  const size = useSpring(targetSize, spring);

  return (
    <motion.div
      ref={ref}
      style={{ width: size, height: size }}
      onHoverStart={() => isHovered.set(1)}
      onHoverEnd={() => isHovered.set(0)}
      onFocus={() => isHovered.set(1)}
      onBlur={() => isHovered.set(0)}
      onClick={onClick}
      className={`relative inline-flex shrink-0 items-center justify-center rounded-lg transition-colors ${className}`}
      tabIndex={0}
      role="button"
    >
      {Children.map(children, (child) =>
        React.isValidElement(child)
          ? cloneElement(child as React.ReactElement<{ isHovered?: MotionValue<number> }>, { isHovered })
          : child,
      )}
    </motion.div>
  );
}

function DockLabel({
  children,
  isHovered,
}: {
  children: React.ReactNode;
  isHovered?: MotionValue<number>;
}) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!isHovered) return;
    const unsub = isHovered.on('change', (latest) => setVisible(latest === 1));
    return () => unsub();
  }, [isHovered]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: 1, y: -10 }}
          exit={{ opacity: 0, y: 0 }}
          transition={{ duration: 0.18 }}
          className="bg-surface border border-separator absolute -top-7 left-1/2 w-fit whitespace-pre rounded-md px-2 py-0.5 text-xs text-ink"
          role="tooltip"
          style={{ x: '-50%' }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function DockIcon({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center justify-center">{children}</div>;
}

export default function Dock({
  items,
  className = '',
  spring = { mass: 0.1, stiffness: 150, damping: 12 },
  magnification = 60,
  distance = 160,
  baseItemSize = 40,
}: DockProps) {
  const { t } = useTranslation();
  const mouseX = useMotionValue(Infinity);
  // Honour the "reduce motion" setting — collapse magnification to the resting
  // size so the icons stay static.
  const reduce = useReduceMotion();
  const mag = reduce ? baseItemSize : magnification;

  return (
    <div
      onMouseMove={reduce ? undefined : (e) => mouseX.set(e.pageX)}
      onMouseLeave={reduce ? undefined : () => mouseX.set(Infinity)}
      // Resting height hugs the base icons; magnified icons grow UPWARD past the
      // bar (overflow-visible) so the resting bar stays compact.
      style={{ height: baseItemSize }}
      className={`flex items-end justify-center gap-3 overflow-visible ${className}`}
      role="toolbar"
      aria-label={t('ui.dock.toolbar')}
    >
      {items.map((item, index) => (
        <DockItem
          key={index}
          onClick={item.onClick}
          className={item.className}
          mouseX={mouseX}
          spring={spring}
          distance={distance}
          magnification={mag}
          baseItemSize={baseItemSize}
        >
          <DockIcon>{item.icon}</DockIcon>
          <DockLabel>{item.label}</DockLabel>
        </DockItem>
      ))}
    </div>
  );
}
